import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, MessageSquare, Fingerprint, Users, PhoneCall, FileCheck2, Bot,
  Zap, ShieldCheck, HeartHandshake, Check, Award, ScrollText, KeyRound, Lock, Server,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import { Button } from "../components/ui/button";
import FSArchitectureFlow from "../components/fs/FSArchitectureFlow";
import RiskSelector from "../components/fs/RiskSelector";
import LogbergDemo from "../components/fs/LogbergDemo";
import AgenticControl from "../components/fs/AgenticControl";
import RegulatoryReality from "../components/fs/RegulatoryReality";
import ControlMapper from "../components/fs/ControlMapper";
import IndustrySelector from "../components/fs/IndustrySelector";
import GovernanceDashboard from "../components/fs/GovernanceDashboard";
import DeploymentExplorer from "../components/defence/DeploymentExplorer";
import FSWalkthroughForm from "../components/fs/FSWalkthroughForm";
import { track } from "../lib/analytics";

const AI_USES = ["Customer service", "Internal copilots", "Advice / support journeys", "Contact centres", "Document processing", "Fraud & financial-crime workflows", "Operations", "Decision support", "AI agents", "Knowledge systems"];
const GOV_QUESTIONS = [
  "What information can users give AI?",
  "What can AI tell a customer?",
  "When should an interaction stop?",
  "When must a human become involved?",
  "How are vulnerable customers identified and supported?",
  "How does the firm know what actually happened?",
  "Can compliance review the decision afterwards?",
  "Can management identify recurring issues?",
];

const EVIDENCE_ROWS = [
  ["Interaction", "Customer chat — product suitability question"],
  ["Timestamp", "Recorded at time of interaction"],
  ["Risk", "Potential advice boundary · Medium"],
  ["Policy", "Suitability & advice-boundary policy"],
  ["Initial action", "HOLD"],
  ["Escalation", "Routed to compliance reviewer"],
  ["Reviewer", "Authorised reviewer (assigned)"],
  ["Reviewer decision", "Reframed as guidance — within policy"],
  ["Final outcome", "Released with guidance · evidence retained"],
];

const OVERSIGHT_TIERS = [
  { level: "Low risk", action: "Continue automatically", tone: "green" },
  { level: "Medium risk", action: "Warn + record", tone: "cyan" },
  { level: "High risk", action: "Hold for review", tone: "amber" },
  { level: "Critical", action: "Block + escalate", tone: "red" },
];
const toneMap = {
  green: "border-signal-green/40 bg-signal-green/[0.06]",
  cyan: "border-primary/40 bg-primary/[0.06]",
  amber: "border-signal-amber/40 bg-signal-amber/[0.06]",
  red: "border-signal-red/40 bg-signal-red/[0.06]",
};

const DUTY_THEMES = [
  { t: "Products & services", q: "Could AI conversations cause customers to misunderstand the product or journey?" },
  { t: "Price & value", q: "Are AI-led journeys producing unexpected customer experiences that should be investigated?" },
  { t: "Consumer understanding", q: "Are customers receiving communications and explanations they can understand?" },
  { t: "Consumer support", q: "Is AI helping customers obtain appropriate support, or creating friction and poor outcomes?" },
];

const VULN_INDICATORS = ["Bereavement", "Financial distress", "Serious illness", "Low financial capability", "Communication difficulty", "Repeated confusion", "Potential coercion", "Statements suggesting acute vulnerability"];

function Flow({ steps, tone = "cyan" }) {
  return (
    <div className="mt-5 space-y-1.5">
      {steps.map((s, i) => (
        <div key={i}>
          <div className="rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/90">{s}</div>
          {i < steps.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
        </div>
      ))}
    </div>
  );
}

function BenefitHead({ n, icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
      <div>
        <span className="block font-mono text-xs text-primary/60">{n}</span>
        <h3 className="text-base font-semibold text-foreground leading-tight">{title}</h3>
      </div>
    </div>
  );
}

export default function FinancialServices() {
  useEffect(() => { document.title = "Financial Services | norse3"; }, []);

  return (
    <div className="overflow-x-clip">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
            <div className="animate-fade-up">
              <Eyebrow>Financial Services</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.3rem] font-bold leading-[1.05] text-foreground text-balance">Use AI at scale. Keep control of every interaction.</h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
                norse3 provides an independent control and oversight layer around AI and customer
                conversations — helping financial-services firms apply their own policies, identify
                designated risks, intervene where required and create reviewable evidence.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/financial-services#walkthrough" onClick={() => track("fs_hero_cta", { cta: "walkthrough" })} data-testid="fs-hero-walkthrough">Request a Financial Services Walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                  <Link to="/financial-services#regulated-interaction" onClick={() => track("fs_hero_cta", { cta: "interaction" })}>See a Regulated Interaction</Link>
                </Button>
              </div>
              <p className="mt-6 text-xs text-muted-foreground/70 max-w-xl">
                norse3 is not a regulator, compliance adviser or a replacement for your compliance
                function, and it is not FCA-approved or a guarantee of compliance.
              </p>
            </div>
            <div id="regulated-interaction" className="animate-fade-up scroll-mt-24 lg:pl-4" style={{ animationDelay: "120ms" }}>
              <FSArchitectureFlow />
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGE */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The challenge"
              title="AI can transform financial services. The accountability stays with the firm."
              subtitle="Firms are increasingly using AI across the business. Governance has to keep pace with where it is being used."
            />
          </Reveal>
          <Reveal className="mt-8" delay={80}>
            <div className="flex flex-wrap gap-2">
              {AI_USES.map((u) => <span key={u} className="rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-sm text-foreground/85">{u}</span>)}
            </div>
          </Reveal>
          <Reveal className="mt-10" delay={120}>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">The governance problem</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {GOV_QUESTIONS.map((q) => (
                    <div key={q} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/90"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {q}</div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8 flex flex-col justify-center">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                  <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-5 text-center">
                    <Zap className="mx-auto h-6 w-6 text-primary" />
                    <p className="mt-2.5 text-base font-bold text-foreground">AI capability</p>
                  </div>
                  <div className="text-2xl font-bold text-muted-foreground/60">↔</div>
                  <div className="rounded-xl border border-signal-amber/30 bg-signal-amber/[0.05] p-5 text-center">
                    <ShieldCheck className="mx-auto h-6 w-6 text-signal-amber" />
                    <p className="mt-2.5 text-base font-bold text-foreground">Firm accountability</p>
                  </div>
                </div>
                <p className="mt-6 text-center text-base font-medium text-foreground">norse3 is designed to put organisation-defined controls between the two.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIX BENEFITS */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="How norse3 helps" title="Six ways to use AI and keep control." /></Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-3 sm:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="01" icon={MessageSquare} title="Control customer-facing AI" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Define what AI can answer automatically and what requires intervention.</p>
                <Flow steps={["Customer question", "Risk assessment", "Firm policy", "Allow / Warn / Hold / Block / Escalate", "Evidence"]} />
                <p className="mt-4 text-xs text-muted-foreground/70">Examples: potential financial advice, product information, customer support, complaints, sensitive information. norse3 does not itself determine whether regulated advice has legally occurred.</p>
              </div>
            </Reveal>
            <Reveal delay={70}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="02" icon={Fingerprint} title="Protect sensitive information" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Apply controls around the information entering and leaving AI systems.</p>
                <div className="mt-5 grid gap-2">
                  {["Account information", "Identity documents", "Financial information", "Personal information", "Internal confidential information"].map((r) => (
                    <div key={r} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2 text-sm text-foreground/90"><span className="h-1.5 w-1.5 rounded-full bg-signal-amber" /> {r}</div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {["Detect", "Warn", "Redact", "Block", "Escalate", "Record"].map((a) => <span key={a} className="rounded-full border border-primary/25 bg-primary/[0.06] px-2.5 py-1 text-xs text-primary">{a}</span>)}
                </div>
              </div>
            </Reveal>
            <Reveal delay={140}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="03" icon={Users} title="Strengthen human oversight" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Put people back into the decisions that matter.</p>
                <div className="mt-5 space-y-2">
                  {OVERSIGHT_TIERS.map((t) => (
                    <div key={t.level} className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 ${toneMap[t.tone]}`}>
                      <span className="text-sm font-medium text-foreground">{t.level}</span>
                      <span className="text-xs font-semibold text-foreground/80">{t.action}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground/70">Thresholds and actions are determined by the organisation's policies and implementation.</p>
              </div>
            </Reveal>
            <Reveal>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="04" icon={PhoneCall} title="Monitor customer interactions" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Move from small samples towards risk-led review with <span className="text-foreground font-medium">Lögberg</span>.</p>
                <div className="mt-5 grid gap-2">
                  {["Screen conversations", "Identify designated risk indicators", "Prioritise interactions", "Route cases to QA / compliance", "Record reviewer decisions", "Produce management information"].map((r) => (
                    <div key={r} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2 text-sm text-foreground/90"><Check className="h-4 w-4 text-primary" /> {r}</div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground/70">Automated screening supports — it does not eliminate — manual QA.</p>
              </div>
            </Reveal>
            <Reveal delay={70}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="05" icon={FileCheck2} title="Create reviewable evidence" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">For every flagged interaction, understand what happened next — connected to Heimdall.</p>
                <div className="mt-5 rounded-xl border border-white/10 bg-navy-800/50 overflow-hidden">
                  {EVIDENCE_ROWS.map(([k, v], i) => (
                    <div key={k} className={`grid grid-cols-[104px_1fr] gap-3 px-3.5 py-1.5 text-xs ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                      <span className="font-mono uppercase tracking-wide text-primary/70">{k}</span>
                      <span className="text-foreground/85">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground/70">Synthetic Heimdall record. Illustrative only.</p>
              </div>
            </Reveal>
            <Reveal delay={140}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="06" icon={Bot} title="Govern employee AI" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Apply organisation-defined controls around how staff use approved AI systems.</p>
                <div className="mt-5 grid gap-2">
                  {["Employee enters sensitive customer information", "Employee uploads restricted documentation", "Employee requests inappropriate customer recommendations", "AI copilot generates potentially unsuitable external content"].map((r) => (
                    <div key={r} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2 text-sm text-foreground/90"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-signal-amber" /> {r}</div>
                  ))}
                </div>
                <Link to="/enterprise-ai" className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">Connect to norse3 Enterprise AI <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RISK SELECTOR */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Find your fit" title="Where is AI creating risk in your business?" subtitle="Pick the closest need and we'll point you to the right part of norse3." /></Reveal>
          <Reveal className="mt-10" delay={80}><RiskSelector /></Reveal>
        </div>
      </section>

      {/* CONSUMER DUTY */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Customer outcomes"
              title="From policy to customer outcome."
              subtitle="Consumer Duty focuses firms on delivering and monitoring customer outcomes. norse3 can support monitoring and evidence processes — it does not itself demonstrate compliance."
            />
          </Reveal>
          <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-4">
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-3">
                {DUTY_THEMES.map((d) => (
                  <div key={d.t} className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
                    <h3 className="text-base font-semibold text-foreground">{d.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d.q}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <Flow steps={["Customer interaction", "norse3 monitoring", "Risk / outcome indicator", "Human review", "Management information", "Firm action"]} />
              </div>
            </Reveal>
          </div>
          <Reveal className="mt-6" delay={140}>
            <p className="rounded-xl border border-white/10 bg-navy-900/60 px-5 py-4 text-sm text-muted-foreground">norse3 can support monitoring and evidence processes. The firm remains responsible for determining its regulatory obligations and whether it is meeting them.</p>
          </Reveal>
        </div>
      </section>

      {/* VULNERABILITY */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Vulnerability"
              title="Support human judgement when vulnerability matters."
              subtitle="Firms may need systems and processes capable of identifying indicators that a customer may require additional support. norse3 surfaces potential indicators — it does not definitively determine whether someone is vulnerable."
            />
          </Reveal>
          <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-4">
            <Reveal>
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Potential vulnerability indicators (synthetic)</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {VULN_INDICATORS.map((v) => (
                    <div key={v} className="flex items-center gap-2.5 rounded-lg border border-signal-amber/25 bg-signal-amber/[0.04] px-3.5 py-2.5 text-sm text-foreground/90"><HeartHandshake className="h-4 w-4 text-signal-amber" /> {v}</div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground/70">Language used is "potential vulnerability indicator", never "vulnerable customer detected".</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <Flow steps={["Indicator detected", "Firm policy", "Continue / adapt / escalate", "Human support", "Outcome recorded"]} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOGBERG */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Lögberg"
              title="Your QA team cannot listen to everything. Lögberg can help determine what they should hear first."
              subtitle="Lögberg screens customer conversations against configured risk indicators and helps QA and compliance teams prioritise interactions for human review."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><LogbergDemo /></Reveal>
        </div>
      </section>

      {/* AGENTIC */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Agentic AI"
              title="When AI starts acting, controls need to act first."
              subtitle="There is a difference between AI that answers and AI that acts. Before a designated action, norse3 evaluates it against your policy."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><AgenticControl /></Reveal>
        </div>
      </section>

      {/* REGULATORY REALITY */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Regulatory reality"
              title="Built for the regulatory reality firms already operate within."
              subtitle="The FCA's current approach to AI uses existing regulatory frameworks rather than a standalone AI rulebook. Select a theme to see the operational question, a possible norse3 control and the evidence it creates."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><RegulatoryReality /></Reveal>
        </div>
      </section>

      {/* CONTROL MAPPER */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Map your environment"
              title="Map your AI control environment."
              subtitle="Answer a few questions to see your current AI journey alongside an illustrative norse3 control journey. This is an illustrative architecture, not a compliance assessment."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><ControlMapper /></Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="By sub-sector" title="Tuned to your corner of financial services." /></Reveal>
          <Reveal className="mt-10" delay={80}><IndustrySelector /></Reveal>
        </div>
      </section>

      {/* GOVERNANCE DASHBOARD */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="For senior management"
              title="Turn AI activity into management information."
              subtitle="How operational interaction data could support governance and oversight. This is a Governance & Outcomes Dashboard, not a regulatory report."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><GovernanceDashboard /></Reveal>
        </div>
      </section>

      {/* DEPLOYMENT */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Deployment"
              title="Fit the control layer around your environment."
              subtitle="Data residency, retention, access, integrations, policy ownership and review permissions are configured to client requirements and technical discovery."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><DeploymentExplorer /></Reveal>
        </div>
      </section>

      {/* PROCUREMENT / TRUST */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Assurance" title="Built for organisations where evidence matters." subtitle="Showing only what is verified today." /></Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Award, t: "ISO 9001", d: "Quality management — certified.", v: true },
              { icon: Award, t: "ISO 42001", d: "AI management system — certified.", v: true },
              { icon: ScrollText, t: "SOC 2", d: "Roadmap / readiness work in progress — not yet certified.", v: false },
              { icon: ShieldCheck, t: "Policy controls", d: "Configurable, versioned organisation-defined policies." },
              { icon: Users, t: "Human oversight", d: "Escalation and final decisions stay with your people." },
              { icon: FileCheck2, t: "Reviewable records", d: "Evidence for each handled interaction." },
              { icon: Lock, t: "Configurable retention", d: "Retention windows configured to your policy." },
              { icon: KeyRound, t: "Configurable access & tenant separation", d: "Role-based access; tenants not commingled by default." },
              { icon: Server, t: "Deployment flexibility", d: "Hosted, private cloud or customer-controlled." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    {x.v === true && <span className="rounded-full border border-signal-green/40 bg-signal-green/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-green">VERIFIED</span>}
                    {x.v === false && <span className="rounded-full border border-signal-amber/40 bg-signal-amber/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-amber">ROADMAP</span>}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8">
            <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
              <Link to="/trust-centre" data-testid="fs-trust-cta">Visit Trust Centre <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WALKTHROUGH */}
      <section id="walkthrough" className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <Eyebrow>Financial services</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-foreground text-balance">Request a Financial Services Walkthrough.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Tell us about your firm and where you'd like more control, and we'll show norse3 against a realistic financial-services scenario.</p>
            <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
              {["Where control could sit around your AI and conversations", "How policy, oversight and evidence apply to your journeys", "How Lögberg could prioritise interactions for your QA team"].map((x) => (
                <li key={x} className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {x}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-3" delay={100}><FSWalkthroughForm /></Reveal>
        </div>
      </section>

      {/* FINAL POSITIONING */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <div className="flex flex-wrap justify-center gap-2">
              {["Use AI", "Protect customers", "Apply your policies", "Keep humans accountable", "Monitor outcomes", "Create evidence"].map((x) => (
                <span key={x} className="rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-sm text-foreground/85">{x}</span>
              ))}
            </div>
            <h2 className="mt-8 text-3xl sm:text-4xl font-bold text-foreground text-balance">AI will change financial services. Accountability does not disappear with it.</h2>
            <p className="mt-4 text-lg text-muted-foreground">Firms should not have to choose between adopting AI and maintaining control.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/financial-services#walkthrough" data-testid="fs-final-cta">Request a Financial Services Walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                <Link to="/interactive-demo">See norse3 in action</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
